import React, { useState, useEffect } from 'react';
import { X, Mail, Gift, BookOpen, CheckCircle, Sparkles, MapPin, Phone, User } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {hasSeenNewsletter, markNewsletterSeen} from '../utils/newsletterSession';

// Validation du numéro de téléphone français
const validatePhoneNumber = (phone) => {
  // Supprimer les espaces, tirets, points
  const cleanPhone = phone.replace(/[\s\-\.]/g, '');
  
  // Vérifier le format français
  // Doit commencer par 0 et avoir 10 chiffres, ou +33 et 11 chiffres
  const frenchMobileRegex = /^(0[67])[0-9]{8}$/;
  const frenchLandlineRegex = /^0[1-9][0-9]{8}$/;
  const internationalRegex = /^\+33[67][0-9]{8}$/;
  
  return frenchMobileRegex.test(cleanPhone) || 
         frenchLandlineRegex.test(cleanPhone) || 
         internationalRegex.test(cleanPhone);
};

const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check if user has already seen the popup in this session
    const hasSeenPopup = hasSeenNewsletter();
    
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000); // 3 seconds delay

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    markNewsletterSeen();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setPhoneError(null);
    
    // Valider le numéro de téléphone
    if (!validatePhoneNumber(phone)) {
      setPhoneError('Veuillez entrer un numéro de téléphone français valide (ex: 06 12 34 56 78)');
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          phone: phone.replace(/[\s\-\.]/g, ''), // Nettoyer le numéro
          postal_code: postalCode,
          first_name: firstName.trim()
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
          markNewsletterSeen();
        
        // Close popup after 3 seconds
        setTimeout(() => {
          setIsOpen(false);
        }, 3000);
      } else if (response.status === 400) {
        setError('Cet email est déjà inscrit !');
      } else {
        throw new Error('Erreur');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={open => { if (!open) handleClose(); }}>
    <DialogPrimitive.Portal>
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <DialogPrimitive.Overlay
        data-testid="newsletter-backdrop"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Popup */}
      <DialogPrimitive.Content aria-describedby={undefined} data-testid="newsletter-dialog" className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          data-testid="newsletter-close"
          aria-label="Fermer l’offre newsletter"
          className="absolute top-2 right-2 sm:top-4 sm:right-4 p-1.5 sm:p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-10"
        >
          <X size={20} className="sm:w-6 sm:h-6" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 px-4 sm:px-8 py-6 sm:py-10 text-white text-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-24 sm:w-32 h-24 sm:h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-16 sm:w-24 h-16 sm:h-24 bg-white/10 rounded-full translate-x-1/2 translate-y-1/2" />
          
          <div className="relative">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-xl sm:rounded-2xl mb-3 sm:mb-4">
              <Gift className="text-white w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <DialogPrimitive.Title asChild><h2 data-testid="newsletter-offer-title" className="text-xl sm:text-2xl md:text-3xl font-black mb-1 sm:mb-2">
              -5% sur votre 1ère location
            </h2></DialogPrimitive.Title>
            <p className="text-green-100 text-sm sm:text-base">
              Rejoignez notre liste VIP exclusive !
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-8 py-5 sm:py-8">
          {isSubmitted ? (
            <div data-testid="newsletter-success" role="status" className="text-center py-2 sm:py-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <CheckCircle className="text-green-500 w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-2">
                Bienvenue dans la famille 3DLoc !
              </h3>
              <p className="text-slate-600 text-sm sm:text-base">
                Vérifiez votre boîte mail pour recevoir votre code promo et notre catalogue.
              </p>
            </div>
          ) : (
            <>
              {/* Benefits */}
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Gift className="text-green-600 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </div>
                  <span className="text-slate-700 text-sm sm:text-base">
                    <strong>-5% de réduction</strong> sur votre première commande
                  </span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BookOpen className="text-green-600 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </div>
                  <span className="text-slate-700 text-sm sm:text-base">
                    <strong>Catalogue à jour</strong> envoyé directement par email
                  </span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="text-green-600 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </div>
                  <span className="text-slate-700 text-sm sm:text-base">
                    <strong>Offres exclusives</strong> et nouveautés en avant-première
                  </span>
                </div>
              </div>

              {/* Form */}
              <form data-testid="newsletter-form" onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {error && (
                  <div data-testid="newsletter-error" role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                    {error}
                  </div>
                )}
                <div className="relative">
                  <User className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Votre prénom"
                    aria-label="Votre prénom"
                    className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base sm:text-lg"
                    data-testid="newsletter-firstname"
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="email"
                    data-testid="newsletter-email"
                    aria-label="Votre adresse email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Votre adresse email"
                    required
                    className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base sm:text-lg"
                  />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="tel"
                    data-testid="newsletter-phone"
                    aria-label="Votre numéro de téléphone"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setPhoneError(null);
                    }}
                    placeholder="Votre numéro de téléphone"
                    required
                    className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 bg-slate-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base sm:text-lg ${phoneError ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                  />
                </div>
                {phoneError && (
                  <div data-testid="newsletter-phone-error" role="alert" className="text-red-500 text-xs sm:text-sm">
                    {phoneError}
                  </div>
                )}
                <div className="relative">
                  <MapPin className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="text"
                    value={postalCode}
                    data-testid="newsletter-postal-code"
                    aria-label="Votre code postal"
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="Votre code postal"
                    pattern="[0-9]{5}"
                    maxLength={5}
                    required
                    className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base sm:text-lg"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  disabled={isLoading}
                  className="w-full py-3 sm:py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-green-500/30 transition-all disabled:opacity-70 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Inscription en cours...
                    </>
                  ) : (
                    <>
                      <Gift size={18} className="sm:w-5 sm:h-5" />
                      Recevoir mon code -5%
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <p className="text-center text-xs text-slate-400 mt-3 sm:mt-4">
                En vous inscrivant, vous acceptez de recevoir nos emails. Désabonnement possible à tout moment.
              </p>
            </>
          )}
        </div>

        {/* Bottom decoration */}
        <div className="h-1 bg-gradient-to-r from-green-400 via-green-500 to-green-600" />
      </DialogPrimitive.Content>
    </div>
    </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default NewsletterPopup;
